import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Col, Modal, Row,
} from 'react-bootstrap';
import Icon from 'components/DesignSystem/Icon';
import { selectFluxAutomaticControlPanelMltOption, selectFluxAutomaticControlPanelSpatialSubdivision } from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { RevisionDataPerScenario, buildSubdivisionParam } from 'helper/flux/automatic';
import useFluxAutomaticZipDownload from 'utils/hooks/flux/automatic/useFluxAutomaticZipDownload';
import CentralizedSpinner from 'components/CentralizedSpinner';
import Snackbar from 'components/DesignSystem/Snackbar';
import './index.scss';
import FluxAutomaticGeneralTable from '../../GeneralTable';

export interface RevisionModal {
  isOpen: boolean
  modelName: string
  period: string
  onClose(): void
  revision: string | undefined
  errorMessage: string | undefined
  data: RevisionDataPerScenario | undefined,
  toFirst: () => void,
  toPrevious: () => void,
  toNext: () => void,
  toLast: () => void,
  isFirstPeriod: boolean,
  isLastPeriod: boolean,
  isFetchingScenarios: boolean,
  /**
   * Eleva o z-index do modal (e do backdrop) acima do `FullscreenTable`. Só necessário quando
   * este modal é aberto de dentro do overlay de tela cheia (RVX Multicenário mobile) — nos
   * demais call sites, elevar o z-index empurra o `DownloadConfirmationDialog` (outro modal,
   * z-index padrão do Bootstrap) para trás deste, tornando-o inacessível. Default `false`.
   */
  aboveFullscreen?: boolean,
}

export default function RevisionSubTableModal({
  isOpen,
  modelName,
  period,
  onClose,
  data,
  errorMessage,
  revision,
  toFirst,
  toPrevious,
  toNext,
  toLast,
  isFirstPeriod,
  isLastPeriod,
  isFetchingScenarios,
  aboveFullscreen = false,
}: RevisionModal) {
  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const subdivision = useSelector(selectFluxAutomaticControlPanelSpatialSubdivision);
  const spatialSubdivisionParam = useMemo(() => buildSubdivisionParam(subdivision), [subdivision]);

  const { DownloadZipButton, DownloadConfirmationDialog } = useFluxAutomaticZipDownload({
    models: [modelName],
    review: revision,
    unidade,
    forecastDate: period,
    type: 'revision',
    pastSimulationNames: [],
    spatialSubdivisionParam,
  });

  const emptyData = {
    colLabels: [],
    rowLabels: [],
    values: [],
  };
  const fallbackObject = {
    bacias: emptyData,
    postos: emptyData,
    sistema: emptyData,
    subsistemas: emptyData,
    reservatorios: emptyData,
  };
  const selectedModelData = data !== undefined && modelName in data
    ? data[modelName]
    : fallbackObject;

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      dialogClassName="default-modal modal-dialog-scrollable flux-sub-table"
      className={aboveFullscreen ? 'flux-sub-table-modal' : undefined}
      backdropClassName={aboveFullscreen ? 'flux-sub-table-backdrop' : undefined}
    >
      <Modal.Header>
        <Modal.Title>
          <Row>
            <Col className="header">
              <span className="model-name">{modelName}</span>
              <span className="revision">{revision}</span>
              <span className="period">{period}</span>
            </Col>
            <Col md={1}>
              <DownloadZipButton />
              <DownloadConfirmationDialog />
            </Col>
          </Row>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isFetchingScenarios && (
          <div className="flux-sub-table-spinner-container">
            <CentralizedSpinner />
          </div>
        )}
        {!isFetchingScenarios && (
          <div className="d-flex flex-row">
            <div className="flux-sub-table-arrow-container">
              <Button
                variant="link"
                size="lg"
                disabled={isFirstPeriod}
                onClick={toFirst}
                className="flux-sub-table-arrow"
              >
                <Icon className="" iconType="double_left_arrow" />
              </Button>
            </div>
            <div className="flux-sub-table-arrow-container">
              <Button
                variant="link"
                size="lg"
                disabled={isFirstPeriod}
                onClick={toPrevious}
                className="flux-sub-table-arrow"
              >
                <Icon className="" iconType="left_arrow" />
              </Button>
            </div>
            <div className="flex-grow-1 mx-3">
              { errorMessage
                ? (
                  <Snackbar variant="danger">
                    {errorMessage}
                  </Snackbar>
                )
                : Object.keys(selectedModelData).map((system) => (
                  <FluxAutomaticGeneralTable
                    data={selectedModelData[system]}
                    lastColAsPercent
                    tableLabel={system}
                    key={system}
                  />
                ))}
            </div>
            <div className="flux-sub-table-arrow-container">
              <Button
                variant="link"
                size="lg"
                disabled={isLastPeriod}
                onClick={toNext}
                className="flux-sub-table-arrow"
              >
                <Icon className="" iconType="right_arrow" />
              </Button>
            </div>
            <div className="flux-sub-table-arrow-container">
              <Button
                variant="link"
                size="lg"
                disabled={isLastPeriod}
                onClick={toLast}
                className="flux-sub-table-arrow"
              >
                <Icon className="" iconType="double_right_arrow" />
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
